/**
 * PLP colour swatches -- swap the product card in place.
 *
 * Colours on this store are separate products grouped by the
 * custom.swatch_group metafield, so the theme's native variant swatch system
 * (xPreviewColorSwatch in preview-color-swatches.js) never binds to them.
 * Without this, every swatch was a plain link straight to the PDP.
 *
 * Clicking a swatch updates the card's image, hover image, title, price and
 * links, and leaves the shopper on the collection page. The anchor href is kept
 * intact so modified clicks (new tab, middle click) and crawlers still work.
 *
 * Two things this has to work around:
 *
 * 1. card_info is rendered twice per card in snippets/card-product.liquid
 *    (grid copy + list-layout copy), so every card has TWO .x-card-swatches
 *    rows, two .x-card-title and two .x-card-price nodes. All copies are
 *    updated together, otherwise they desync when the breakpoint changes.
 *
 * 2. Quick view bakes the product URL into its Alpine attribute at render time
 *    (loadChooseOptions('/products/...-black', ...)). Alpine compiles that
 *    expression on init, so rewriting the attribute afterwards does nothing.
 *    Instead we wrap the store methods and substitute the URL for cards that
 *    have a colour selected -- otherwise picking Navy and hitting quick-add
 *    would open Black.
 */
if (!window.Eurus.loadedScript.has('onebone-plp-swatches.js')) {
  window.Eurus.loadedScript.add('onebone-plp-swatches.js');

  (function () {
    'use strict';

    var SWATCH = '.x-card-swatch';
    var CARD = '.card-product';
    var SELECTED_ATTR = 'data-swatch-selected-url';

    var quickViewPatched = false;

    /**
     * Quick view resolves the card from the button it was handed, so we can
     * redirect the request to whichever colour that card currently shows.
     */
    function patchQuickView() {
      if (quickViewPatched) return;
      if (typeof Alpine === 'undefined' || !Alpine.store) return;

      var store = Alpine.store('xQuickView');
      if (!store) return;

      ['load', 'loadChooseOptions'].forEach(function (name) {
        var original = store[name];
        if (typeof original !== 'function' || original.__obPatched) return;

        var wrapped = function (url, el, optionId, index) {
          var card = el && el.closest ? el.closest(CARD) : null;
          var selected = card && card.getAttribute(SELECTED_ATTR);
          return original.call(this, selected || url, el, optionId, index);
        };
        wrapped.__obPatched = true;
        store[name] = wrapped;
      });

      quickViewPatched = true;
    }

    function setImage(img, src, srcset) {
      if (!img || !src) return;
      img.removeAttribute('srcset');
      img.removeAttribute('data-srcset');
      if (srcset) img.setAttribute('srcset', srcset);
      img.setAttribute('src', src);
      // the theme's skeleton shimmer is cleared onload, which has already fired
      img.classList.remove('animate-Xpulse', 'skeleton-image');
    }

    function applySwatch(card, swatch) {
      var d = swatch.dataset;

      // Carousel mode builds multiple slides per product; a single-image swap
      // would leave stale slides behind, so leave those cards alone.
      if (card.querySelector('.x-splide')) return false;

      var main = card.querySelector('.preview-img') || card.querySelector('.image-first-hover');
      setImage(main, d.swatchImg, d.swatchImgSrcset);

      var hover = card.querySelector('.image-second-hover, .image-first-hover-slide-anm');
      if (hover) {
        if (d.swatchHoverImg) {
          hover.style.removeProperty('visibility');
          setImage(hover, d.swatchHoverImg);
        } else {
          // this colour has no second image -- don't leave the previous one behind
          hover.style.visibility = 'hidden';
        }
      }

      if (d.swatchUrl) {
        card.setAttribute(SELECTED_ATTR, d.swatchUrl);
        card.querySelectorAll('.link-product-variant').forEach(function (a) {
          a.setAttribute('href', d.swatchUrl);
        });
      }

      if (d.swatchTitle) {
        card.querySelectorAll('.x-card-title').forEach(function (t) {
          t.textContent = d.swatchTitle;
        });
      }

      if (d.swatchPriceHtml) {
        card.querySelectorAll('.x-card-price').forEach(function (wrap) {
          // keep the theme's .next-price placeholder, replace everything else
          var keep = wrap.querySelector('.next-price');
          wrap.innerHTML = d.swatchPriceHtml;
          if (keep) wrap.appendChild(keep);
        });
      }

      // Quick view reads .current-variant to build its fetch URL, and single
      // variant cards post the hidden input straight to /cart/add. Both still
      // hold the previous colour's variant id until we move them over.
      if (d.swatchVariantId) {
        card.querySelectorAll('.current-variant').forEach(function (n) {
          n.innerText = d.swatchVariantId;
        });
        card
          .querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"]')
          .forEach(function (i) {
            i.value = d.swatchVariantId;
          });
      }

      // sync active state across both duplicated swatch rows
      card.querySelectorAll(SWATCH).forEach(function (s) {
        var active = s.dataset.swatchPid === d.swatchPid;
        s.classList.toggle('is-active', active);
        if (active) {
          s.setAttribute('aria-current', 'true');
        } else {
          s.removeAttribute('aria-current');
        }
      });

      return true;
    }

    document.addEventListener(
      'click',
      function (e) {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

        var swatch = e.target.closest && e.target.closest(SWATCH);
        if (!swatch) return;

        var card = swatch.closest(CARD);
        if (!card) return;

        patchQuickView();

        // If the swap can't be applied, fall through to the link so the shopper
        // still reaches the colour they asked for.
        if (!applySwatch(card, swatch)) return;

        e.preventDefault();
        e.stopPropagation();
      },
      true // capture: run before the card's own navigation handlers
    );
  })();
}
